/* Product page interactivity: gallery thumbs/nav, informative variant info (showroom), view_item, sticky CTA. */
(function () {
  'use strict';
  const BB = window.BB;

  class BBGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bb-gallery__track');
      this.items = Array.from(this.querySelectorAll('.bb-gallery__item'));
      this.thumbs = Array.from(this.querySelectorAll('.bb-gallery__thumb'));
      this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.counterTpl = this.counter ? this.counter.textContent : '';
      this.interacted = false;
      this.zoomButtons = Array.from(this.querySelectorAll('[data-gallery-zoom]'));
      this.dialog = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
      this.lightboxCounter = this.querySelector('[data-gallery-lightbox-counter]');
      this.lightboxIndex = 0;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(document.getElementById(t.getAttribute('data-media-target')))));
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      this.zoomButtons.forEach((button, index) => button.addEventListener('click', () => this.openLightbox(index)));
      const lbClose = this.querySelector('[data-gallery-lightbox-close]');
      const lbPrev = this.querySelector('[data-gallery-lightbox-prev]');
      const lbNext = this.querySelector('[data-gallery-lightbox-next]');
      if (lbClose) lbClose.addEventListener('click', () => this.dialog?.close());
      if (lbPrev) lbPrev.addEventListener('click', () => this.stepLightbox(-1));
      if (lbNext) lbNext.addEventListener('click', () => this.stepLightbox(1));
      if (this.dialog) {
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
        this.dialog.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.stepLightbox(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.stepLightbox(-1); }
        });
      }
      this.track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.step(1); if (e.key === 'ArrowLeft') this.step(-1); });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio > 0.6) this.setActive(en.target); });
        }, { root: this.track, threshold: [0.6] });
        this.items.forEach((i) => this.io.observe(i));
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    current() { const index = this.items.findIndex((i) => i.classList.contains('is-active')); return Math.max(0, index); }
    step(dir) { const idx = Math.min(this.items.length - 1, Math.max(0, this.current() + dir)); this.goTo(this.items[idx]); }
    goTo(el) { if (el) { const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' }); } }
    setActive(el) {
      const idx = this.items.indexOf(el);
      const wasActive = this.items.some((i) => i.classList.contains('is-active'));
      this.items.forEach((i) => i.classList.toggle('is-active', i === el));
      this.thumbs.forEach((t) => t.setAttribute('aria-pressed', String(t.getAttribute('data-media-target') === el.id)));
      const activeThumb = this.thumbs[idx];
      if (activeThumb && this.thumbsEl) {
        const thumb = activeThumb.getBoundingClientRect();
        const strip = this.thumbsEl.getBoundingClientRect();
        if (thumb.left < strip.left || thumb.right > strip.right) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.thumbsEl.scrollBy({
            left: thumb.left - strip.left - (strip.width - thumb.width) / 2,
            behavior: reduce ? 'auto' : 'smooth'
          });
        }
      }
      if (this.counter && idx >= 0) this.counter.textContent = this.counterTpl.replace(/^\d+/, String(idx + 1));
      if (wasActive && idx > 0 && !this.interacted) { this.interacted = true; const pr = this.closest('[data-product-id]'); BB.track('bb_product_interaction', { interaction: 'gallery_interaction', placement: 'gallery', product_id: pr ? pr.getAttribute('data-product-id') : null, media_index: idx + 1 }, 'gallery'); }
    }
    openLightbox(index) {
      if (!this.dialog || !this.zoomButtons.length || typeof this.dialog.showModal !== 'function') return;
      this.lightboxIndex = index;
      this.renderLightbox();
      this.dialog.showModal();
    }
    stepLightbox(dir) {
      if (!this.zoomButtons.length) return;
      this.lightboxIndex = (this.lightboxIndex + dir + this.zoomButtons.length) % this.zoomButtons.length;
      this.renderLightbox();
    }
    renderLightbox() {
      const button = this.zoomButtons[this.lightboxIndex];
      if (!button || !this.lightboxImage) return;
      this.lightboxImage.src = button.getAttribute('data-gallery-src') || '';
      this.lightboxImage.alt = button.getAttribute('data-gallery-alt') || '';
      if (this.lightboxCounter) this.lightboxCounter.textContent = (this.lightboxIndex + 1) + ' / ' + this.zoomButtons.length;
    }
    showMedia(id) { const el = this.querySelector('[data-media-id="' + id + '"]'); if (el) this.goTo(el); }
  }
  if (!customElements.get('bb-gallery')) customElements.define('bb-gallery', BBGallery);

  /* Showroom: variant chips are informative only — show the matching price indication, never a buy action. */
  class BBVariantInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-json]');
      this.priceEl = this.querySelector('[data-variant-price]');
      if (!json || !this.priceEl) return;
      try { this.variants = JSON.parse(json.textContent); } catch (e) { return; }
      this.labelEl = this.querySelector('[data-variant-price-label]');
      this.addEventListener('change', () => this.update());
      this.update();
    }
    update() {
      const isTopper = this.hasAttribute('data-topper-price');
      const chosen = Array.from(this.querySelectorAll('fieldset')).map((f) => {
        const select = f.querySelector('select');
        if (select) return select.value || null;
        const radio = f.querySelector('input:checked');
        return radio ? radio.value : null;
      });
      const root = isTopper ? this.closest('.bb-product[data-product-id]') : null;
      if (chosen.length === 0 || chosen.some((v) => !v)) {
        if (root) delete root.dataset.selectedVariantId;
        if (isTopper) {
          this.priceEl.textContent = this.dataset.fromPrice || '';
          if (this.labelEl) this.labelEl.textContent = this.dataset.fromLabel || '';
          this.priceEl.hidden = false;
        } else this.priceEl.hidden = true;
        return;
      }
      const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      if (variant) {
        this.priceEl.textContent = BB.formatMoney(variant.price);
        if (this.labelEl) this.labelEl.textContent = this.dataset.selectedLabel || '';
        this.priceEl.hidden = false;
        if (root) root.dataset.selectedVariantId = String(variant.id);
      } else {
        this.priceEl.hidden = true;
        if (root) delete root.dataset.selectedVariantId;
      }
    }
  }
  if (!customElements.get('bb-variant-info')) customElements.define('bb-variant-info', BBVariantInfo);

  /* Details & specificaties: on desktop every group is open and the summary is not a toggle (one compact list);
     on mobile the groups are +/− accordions (first open by default, from the markup). */
  class BBDetailsGroups extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll('details'));
      this.mq = window.matchMedia('(min-width: 1024px)');
      this.onChange = () => this.apply();
      this.mq.addEventListener('change', this.onChange);
      this.apply();
    }
    disconnectedCallback() { this.mq.removeEventListener('change', this.onChange); }
    apply() {
      const desktop = this.mq.matches;
      const mobileClosed = this.hasAttribute('data-mobile-closed');
      this.classList.toggle('is-static', desktop);
      this.groups.forEach((d, i) => {
        if (desktop) { d.setAttribute('data-bb-auto-open', ''); d.open = true; }
        else { d.setAttribute('data-bb-auto-open', ''); d.open = mobileClosed ? false : i === 0; }
        setTimeout(() => d.removeAttribute('data-bb-auto-open'), 0);
        const summary = d.querySelector('summary');
        if (summary) { summary.tabIndex = desktop ? -1 : 0; summary.setAttribute('aria-disabled', String(desktop)); }
      });
    }
  }
  if (!customElements.get('bb-details-groups')) customElements.define('bb-details-groups', BBDetailsGroups);

  /* view_item once, sticky CTA visibility after the primary CTA scrolls out. */
  function init() {
    const root = document.querySelector('.bb-product[data-product-id]');
    if (!root) return;
    const ctx = document.getElementById('bb-page-context');
    let c = {};
    try { c = ctx ? JSON.parse(ctx.textContent) : {}; } catch (e) {}
    const priceEl = root.querySelector('.bb-price__current, [data-variant-price]');
    BB.track('view_item', {
      item_id: String(c.product_id || root.getAttribute('data-product-id')),
      item_name: c.product_title || document.title,
      item_brand: c.brand || null,
      item_category: c.product_type || null,
      price: priceEl ? priceEl.textContent.trim() : (root.dataset.priceCents ? BB.formatMoney(Number(root.dataset.priceCents)) : null),
      currency: BB.config.currency,
      product_mode: root.getAttribute('data-product-mode')
    }, 'pdp');

    /* Sticky CTA: visible once the primary CTA has scrolled out; hidden while a booking zone is in view, while a dialog/menu
       is open (body.bb-scroll-lock) and on desktop. Body spacing (html.has-sticky-cta) only while it is really shown. */
    const sticky = document.querySelector('[data-sticky-cta]');
    const primary = root.querySelector('.bb-appointment-zone [data-bb-appointment], .bb-appointment-zone [data-bb-appointment-pending], .bb-matras-v2__hero-actions [data-bb-appointment], .bb-buy [type="submit"]');
    if (sticky && primary && 'IntersectionObserver' in window) {
      const zones = Array.from(document.querySelectorAll('.bb-appointment-zone, .bb-appointment-cta-section, [data-appointment-host], [data-bb-mattress-showroom], [data-bb-store-grid]'));
      const mobile = window.matchMedia('(max-width: 1023px)');
      const state = { primary: true, zone: false };
      const apply = () => {
        const show = mobile.matches && !state.primary && !state.zone && !document.body.classList.contains('bb-scroll-lock');
        sticky.hidden = !show;
        document.documentElement.classList.toggle('has-sticky-cta', show);
      };
      new IntersectionObserver((entries) => { state.primary = entries[0].isIntersecting; apply(); }, { threshold: 0 }).observe(primary);
      if (zones.length) {
        const seen = new Map();
        const zoneIO = new IntersectionObserver((entries) => {
          entries.forEach((en) => seen.set(en.target, en.isIntersecting && !en.target.hidden));
          state.zone = Array.from(seen.values()).some(Boolean);
          apply();
        }, { threshold: 0.15 });
        zones.forEach((z) => zoneIO.observe(z));
      }
      new MutationObserver(apply).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      mobile.addEventListener('change', apply);
      window.addEventListener('orientationchange', () => setTimeout(apply, 150));
    }

    /* Progressive disclosure for the craft steps (mobile). */
    document.querySelectorAll('[data-craft-more]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('.bb-craft');
        const open = wrap.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.getAttribute('data-label-less') : btn.getAttribute('data-label-more');
        if (open) BB.track('bb_product_interaction', { interaction: 'craft_expand', placement: 'craft', product_id: root.getAttribute('data-product-id') }, 'craft');
      });
    });

    /* Story excerpt: "Lees verder" reveals the remaining paragraphs. */
    document.querySelectorAll('[data-story-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const more = btn.parentElement.querySelector('[data-story-more]');
        const open = more.hidden;
        more.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.getAttribute('data-label-less') : btn.getAttribute('data-label-more');
      });
    });

    /* FAQ / info accordion opens (user-initiated only; the desktop details layout opens groups programmatically). */
    document.querySelectorAll('details[data-bb-track]').forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open && !d.hasAttribute('data-bb-auto-open')) BB.track('bb_product_interaction', { interaction: d.getAttribute('data-bb-track'), placement: d.getAttribute('data-bb-placement') || 'product_info', product_id: root.getAttribute('data-product-id'), item: d.getAttribute('data-bb-track-item') || null });
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
